import {
  Accordion,
  Badge,
  Col,
  FloatingLabel,
  Row,
  Table,
  Modal,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";

import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import forecasts from "../../../assets/data/forecasts.json";
import axios from "axios";
import moment from "moment/moment";

export default function AllFamilyFundForecast() {

  // =========================================================
  // STATES
  // =========================================================

  const [show, setShow] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  const [reqs, setReqs] = useState([]);

  const [data, setData] = useState({
    thead: [],
    tbody: [],
  });

  // Only this accordion body will render
  const [activeAccordion, setActiveAccordion] = useState("0");

  // Loading state
  const [loading, setLoading] = useState(false);

  // API error
  const [apiError, setApiError] = useState("");

  // Filter error
  const [error, setError] = useState("");

  const [filterValues, setFilterValues] = useState({
    from_date: "",
    to_date: "",
  });

  const AuthToken = localStorage.getItem("AuthUser");


  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {

    setData(
      forecasts || {
        thead: [],
        tbody: [],
      }
    );

    fetchReqs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // =========================================================
  // FETCH REQUESTS
  // =========================================================

  const fetchReqs = async (params = {}) => {

    try {

      setLoading(true);
      setApiError("");

      const response = await axios.get(
        process.env.REACT_APP_API_URL + "/list-forecast",
        {
          params,
          headers: {
            Authorization: `Bearer ${AuthToken}`,
          },
        }
      );

      const result =
        response?.data?.family_requests || [];

      setReqs(result);

      // Open first accordion after load
      if (result.length > 0) {
        setActiveAccordion("0");
      } else {
        setActiveAccordion(null);
      }

    } catch (err) {

      console.error(
        "Error fetching forecasts list:",
        err
      );

      setReqs([]);

      setApiError(
        err?.response?.data?.message ||
        "Unable to load Family Fund Forecast."
      );

    } finally {

      setLoading(false);

    }
  };


  // =========================================================
  // FILTER CHANGE
  // =========================================================

  const handleChange = (e) => {

    const {
      name,
      value,
      type,
      files,
    } = e.target;

    const fieldValue =
      type === "file"
        ? files?.[0]
        : value;

    const updated = {
      ...filterValues,
      [name]: fieldValue,
    };

    setFilterValues(updated);

    const {
      from_date,
      to_date,
    } = updated;

    if (
      (
        from_date &&
        to_date &&
        new Date(to_date) >=
        new Date(from_date)
      ) ||
      (!from_date && !to_date)
    ) {
      setError("");
    }
  };


  // =========================================================
  // APPLY FILTER
  // =========================================================

  const handleApplyFilter = async (e) => {

    if (e?.preventDefault) {
      e.preventDefault();
    }

    const {
      from_date,
      to_date,
    } = filterValues;


    if (!from_date && !to_date) {

      setError(
        "Please enter From Date and To Date."
      );

      return;
    }


    if (from_date && !to_date) {

      setError(
        "Please select a 'To Date' when 'From Date' is chosen."
      );

      return;
    }


    if (!from_date && to_date) {

      setError(
        "Please select a 'From Date' when 'To Date' is chosen."
      );

      return;
    }


    if (
      from_date &&
      to_date &&
      new Date(to_date) <
      new Date(from_date)
    ) {

      setError(
        "'To Date' cannot be earlier than 'From Date'."
      );

      return;
    }


    setError("");

    await fetchReqs({
      from_date,
      to_date,
    });
  };


  // =========================================================
  // CLEAR FILTER
  // =========================================================

  const handleClearFilter = async () => {

    setFilterValues({
      from_date: "",
      to_date: "",
    });

    setError("");
    setApiError("");

    await fetchReqs();
  };


  // =========================================================
  // IMAGE MODAL
  // =========================================================

  const handleShow = (imageSrc) => {

    if (!imageSrc) {
      return;
    }

    setCurrentImage(imageSrc);
    setShow(true);
  };


  const handleClose = () => {

    setShow(false);
    setCurrentImage(null);
  };


  // =========================================================
  // UI
  // =========================================================

  return (

    <PageLayout>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <Row>

        <Col xl={12}>

          <div className="mc-card">

            <div className="mc-breadcrumb">

              <h3 className="mc-breadcrumb-title">
                All Family Fund Forecast
              </h3>

              <ul className="mc-breadcrumb-list">

                <li className="mc-breadcrumb-item">

                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                    style={{
                      color: "#077834",
                    }}
                  >
                    home
                  </Link>

                </li>

                <li className="mc-breadcrumb-item">

                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                    style={{
                      color: "#077834",
                    }}
                  >
                    Finances
                  </Link>

                </li>

                <li className="mc-breadcrumb-item">
                  All Family Fund Forecast
                </li>

              </ul>

            </div>

          </div>

        </Col>

      </Row>


      {/* =====================================================
          FILTER
      ===================================================== */}

      <Row>

        <Col xl={12}>

          <div className="mc-card">

            <div
              className="mc-breadcrumb"
              style={{
                marginBottom: "10px",
              }}
            >

              <h3 className="mc-breadcrumb-title">
                Filter by Date
              </h3>

            </div>


            <Form onSubmit={handleApplyFilter}>

              <Row className="align-items-end">


                {/* FROM DATE */}

                <Col md={4}>

                  <Form.Group>

                    <FloatingLabel label="From">

                      <Form.Control
                        type="date"
                        name="from_date"
                        value={
                          filterValues.from_date
                        }
                        onChange={handleChange}
                        disabled={loading}
                      />

                    </FloatingLabel>

                  </Form.Group>

                </Col>


                {/* TO DATE */}

                <Col md={4}>

                  <Form.Group>

                    <FloatingLabel label="To">

                      <Form.Control
                        type="date"
                        name="to_date"
                        value={
                          filterValues.to_date
                        }
                        onChange={handleChange}
                        disabled={loading}
                      />

                    </FloatingLabel>

                  </Form.Group>

                </Col>


                {/* SEARCH */}

                <Col md={2}>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    style={{
                      paddingTop: "10px",
                      fontWeight: "bold",
                      fontSize: "14px",
                      backgroundColor: "#077834",
                      borderColor: "#077834",
                    }}
                    className="w-100"
                  >

                    {loading ? (

                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading...
                      </>

                    ) : (

                      "Search"

                    )}

                  </Button>

                </Col>


                {/* CLEAR */}

                <Col md={2}>

                  <Button
                    variant="secondary"
                    onClick={handleClearFilter}
                    type="button"
                    disabled={loading}
                    style={{
                      paddingTop: "10px",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}
                    className="w-100"
                  >
                    Clear Filter
                  </Button>

                </Col>

              </Row>


              {/* FILTER ERROR */}

              {error && (

                <div
                  className="text-danger mt-2"
                  style={{
                    fontSize: "13px",
                  }}
                >
                  {error}
                </div>

              )}

            </Form>

          </div>

        </Col>

      </Row>


      {/* =====================================================
          API ERROR
      ===================================================== */}

      {apiError && (

        <Row>

          <Col xl={12}>

            <div className="alert alert-danger">

              {apiError}

            </div>

          </Col>

        </Row>

      )}


      {/* =====================================================
          MAIN LOADING
      ===================================================== */}

      {loading ? (

        <Row>

          <Col xl={12}>

            <div
              className="mc-card"
              style={{
                minHeight: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <div className="text-center">

                <Spinner
                  animation="border"
                  role="status"
                  style={{
                    width: "3rem",
                    height: "3rem",
                  }}
                />

                <div
                  style={{
                    marginTop: "15px",
                    fontWeight: "600",
                  }}
                >
                  Loading Family Fund Forecast...
                </div>

              </div>

            </div>

          </Col>

        </Row>

      ) : (

        <>

          {/* =================================================
              NO RECORDS
          ================================================= */}

          {reqs.length === 0 && !apiError ? (

            <Row>

              <Col xl={12}>

                <div className="mc-card text-center">

                  <div
                    style={{
                      padding: "40px",
                    }}
                  >
                    No records found.
                  </div>

                </div>

              </Col>

            </Row>

          ) : (

            /* ===============================================
               ACCORDION
            =============================================== */

            <Row>

              <Col xl={12}>

                <Accordion
                  activeKey={activeAccordion}
                  onSelect={(eventKey) => {

                    setActiveAccordion(
                      eventKey
                    );

                  }}
                >

                  {reqs.map(
                    (req, index) => {

                      /*
                       * Only calculate summary total.
                       * This does NOT create family table rows.
                       */

                      const totalFamilyAmount =
                        req.families?.reduce(
                          (
                            sum,
                            item
                          ) => {

                            const loanAmount =
                              Number(
                                item.person
                                  ?.loan
                                  ?.loan_amount ||
                                0
                              );

                            const resettlementAmount =
                              Number(
                                item.person
                                  ?.resettlement_amount ||
                                0
                              );

                            const estimatedCost =
                              Number(
                                item.person
                                  ?.loan
                                  ?.estimated_business_cost ||
                                0
                              );

                            const scholarshipAmount =
                              Number(
                                item.person
                                  ?.loan
                                  ?.scholarship_amount ||
                                0
                              );

                            return (
                              sum +
                              loanAmount +
                              resettlementAmount +
                              estimatedCost +
                              scholarshipAmount
                            );

                          },
                          0
                        ) || 0;


                      return (

                        <Accordion.Item
                          key={req.id}
                          eventKey={index.toString()}
                          className="custom-accordion"
                        >

                          {/* ===============================
                              ACCORDION HEADER
                          =============================== */}

                          <Accordion.Header>

                            <Table
                              bordered
                              hover
                              size="sm"
                              className="
                                text-center
                                mb-0
                                head-table
                                table-responsive
                              "
                            >

                              <thead className="table-light">

                                <tr>

                                  <th>
                                    REQ-ID
                                  </th>

                                  <th>
                                    TOTAL FAMILIES
                                  </th>

                                  <th>
                                    TOTAL AMOUNT
                                  </th>

                                  <th>
                                    STATUS
                                  </th>

                                  <th>
                                    DATE REQUESTED
                                  </th>

                                  <th>
                                    ACTION
                                  </th>

                                </tr>

                              </thead>


                              <tbody>

                                <tr className="border rounded">

                                  <td>
                                    {req.rfq_id}
                                  </td>

                                  <td>
                                    {
                                      req.families
                                        ?.length || 0
                                    }
                                  </td>

                                  <td>
                                    {
                                      totalFamilyAmount
                                    }
                                  </td>

                                  <td>

                                    <Badge
                                      bg={
                                        req.status_id ===
                                        5
                                          ? "success"
                                          : "warning"
                                      }
                                    >
                                      {
                                        req.status
                                          ?.name
                                      }
                                    </Badge>

                                  </td>

                                  <td>

                                    {moment(
                                      req.created_at
                                    ).format(
                                      "DD-MM-YYYY"
                                    )}

                                  </td>

                                  <td>

                                    <Link
                                      to={`/edit-family-fund-forecast/${req.id}`}
                                    >

                                      <i
                                        className="material-icons text-center"
                                        title="Edit"
                                      >
                                        edit_note
                                      </i>

                                    </Link>

                                  </td>

                                </tr>

                              </tbody>

                            </Table>

                          </Accordion.Header>


                          {/* ===============================
                              ACCORDION BODY

                              IMPORTANT:
                              ONLY ACTIVE BODY IS RENDERED
                          =============================== */}

                          <Accordion.Body>

                            {activeAccordion ===
                              index.toString() && (

                              <div className="mc-table-responsive">

                                <table className="mc-table product">

                                  <thead className="mc-table-head primary">

                                    <tr>

                                      {data?.thead?.map(
                                        (
                                          item,
                                          idx
                                        ) => (

                                          <th
                                            key={
                                              idx
                                            }
                                            style={{
                                              padding:
                                                "0.5rem 1rem",
                                            }}
                                          >
                                            {
                                              item
                                            }
                                          </th>

                                        )
                                      )}

                                    </tr>

                                  </thead>


                                  <tbody className="mc-table-body even">

                                    {req.families?.map(
                                      (
                                        item,
                                        idx
                                      ) => {

                                        const loanAmount =
                                          Number(
                                            item.person
                                              ?.loan
                                              ?.loan_amount ||
                                            0
                                          );

                                        const resettlementAmount =
                                          Number(
                                            item.person
                                              ?.resettlement_amount ||
                                            0
                                          );

                                        const estimatedCost =
                                          Number(
                                            item.person
                                              ?.loan
                                              ?.estimated_business_cost ||
                                            0
                                          );

                                        const scholarshipAmount =
                                          Number(
                                            item.person
                                              ?.loan
                                              ?.scholarship_amount ||
                                            0
                                          );

                                        const totalAmount =
                                          loanAmount +
                                          resettlementAmount +
                                          estimatedCost +
                                          scholarshipAmount;


                                        return (

                                          <tr
                                            key={
                                              item.id ||
                                              idx
                                            }
                                          >

                                            {/* REQUEST */}

                                            <td>
                                              {
                                                req.rfq_id
                                              }
                                            </td>


                                            {/* FAMILY NUMBER */}

                                            <td>
                                              {
                                                item.person
                                                  ?.f_number
                                              }
                                            </td>


                                            {/* BRICK KILN */}

                                            <td>
                                              {
                                                item.person
                                                  ?.brick_kiln
                                                  ?.brickkiln_name
                                              }
                                            </td>


                                            {/* OWNER */}

                                            <td>
                                              {
                                                item.person
                                                  ?.brick_kiln
                                                  ?.brickkiln_owner
                                              }
                                            </td>


                                            {/* OWNER CELL */}

                                            <td>
                                              {
                                                item.person
                                                  ?.brick_kiln
                                                  ?.brickkiln_owner_cell
                                              }
                                            </td>


                                            {/* OWNER CNIC */}

                                            <td>
                                              {
                                                item.person
                                                  ?.brick_kiln
                                                  ?.owner_cnic
                                              }
                                            </td>


                                            {/* IDENTIFIER */}

                                            <td>
                                              {
                                                item.person
                                                  ?.identifier
                                                  ?.name
                                              }
                                            </td>


                                            {/* BENEFICIARY */}

                                            <td>
                                              {
                                                item.person
                                                  ?.beneficiary_name
                                              }
                                            </td>


                                            {/* CNIC */}

                                            <td>
                                              {
                                                item.person
                                                  ?.cnic
                                              }
                                            </td>


                                            {/* INCENTIVE */}

                                            <td>
                                              {
                                                item.person
                                                  ?.loan
                                                  ?.incentive
                                              }
                                            </td>


                                            {/* LOAN */}

                                            <td>

                                              {
                                                loanAmount
                                              }


                                              {item.person
                                                ?.loan
                                                ?.unpaid_loan_invoice && (

                                                <>

                                                  <hr />

                                                  <i
                                                    className="material-icons text-center"
                                                    title="View Invoice"
                                                    style={{
                                                      cursor:
                                                        "pointer",
                                                    }}
                                                    onClick={() =>
                                                      handleShow(
                                                        process.env
                                                          .REACT_APP_IMG_URL +
                                                        item
                                                          .person
                                                          .loan
                                                          .unpaid_loan_invoice
                                                      )
                                                    }
                                                  >
                                                    receipt_long
                                                  </i>

                                                </>

                                              )}

                                            </td>


                                            {/* RESETTLEMENT */}

                                            <td>
                                              {
                                                resettlementAmount
                                              }
                                            </td>


                                            {/* BUSINESS COST */}

                                            <td>
                                              {
                                                estimatedCost
                                              }
                                            </td>


                                            {/* SCHOLARSHIP */}

                                            <td>
                                              {
                                                scholarshipAmount
                                              }
                                            </td>


                                            {/* TOTAL */}

                                            <td>
                                              {
                                                totalAmount
                                              }
                                            </td>

                                          </tr>

                                        );

                                      }
                                    )}

                                  </tbody>

                                </table>

                              </div>

                            )}

                          </Accordion.Body>

                        </Accordion.Item>

                      );

                    }
                  )}

                </Accordion>

              </Col>

            </Row>

          )}

        </>

      )}


      {/* =====================================================
          INVOICE MODAL
      ===================================================== */}

      <Modal
        show={show}
        size="lg"
        onHide={handleClose}
        centered
      >

        <Modal.Header closeButton>

          <Modal.Title>
            Invoice Preview
          </Modal.Title>

        </Modal.Header>


        <Modal.Body>

          {currentImage ? (

            <img
              src={currentImage}
              alt="Invoice"
              className="img-fluid"
            />

          ) : (

            <p>
              No Image Available
            </p>

          )}

        </Modal.Body>


        <Modal.Footer>

          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Close
          </Button>

        </Modal.Footer>

      </Modal>

    </PageLayout>

  );
}